import { type createBrowserRouter, IDLE_NAVIGATION } from 'react-router-dom'
import {
  appendOrganizationParam,
  getEnforcedUrlOrganizationId,
  readOrganizationParam,
  subscribeToTabOrganization,
} from '@/lib/tab-organization'

type AppRouter = ReturnType<typeof createBrowserRouter>

export const AUTHENTICATED_LAYOUT_ROUTE_ID = 'authenticated-layout'

/**
 * Puts `?org=` back on any authenticated URL that lacks it: `navigate()`, `<Navigate>`, a
 * `setSearchParams` that rebuilt the query, an OAuth return, an old bookmark. It reads the router's
 * latest location instead of a component's render-time one, so it never undoes a param a page
 * wrote in the same commit. `replace` keeps history, `state` and hash intact.
 */
export function keepOrganizationInUrl(router: AppRouter): () => void {
  const addMissingOrganization = () => {
    const { location, matches, navigation } = router.state
    if (navigation.state !== IDLE_NAVIGATION.state) return
    if (!matches.some((match) => match.route.id === AUTHENTICATED_LAYOUT_ROUTE_ID)) return
    if (readOrganizationParam(location.search) !== null) return
    const organizationId = getEnforcedUrlOrganizationId()
    if (organizationId === null) return
    router.navigate(
      {
        pathname: location.pathname,
        search: appendOrganizationParam(location.search, organizationId),
        hash: location.hash,
      },
      { replace: true, state: location.state, preventScrollReset: true },
    )
  }
  const scheduleCheck = () => queueMicrotask(addMissingOrganization)
  const unsubscribeRouter = router.subscribe(scheduleCheck)
  const unsubscribeStore = subscribeToTabOrganization(scheduleCheck)
  scheduleCheck()
  return () => {
    unsubscribeRouter()
    unsubscribeStore()
  }
}
